/**
 * Where each card sits, as keys rather than records.
 *
 * A kanban rebuilds its lanes whenever anything about a record changes. Most edits — a renamed job,
 * a new note — do not move the card anywhere, so rebuilding every lane to show one new title is the
 * wrong price. Splitting placement (which lane, which column) from content (the record itself) lets
 * a data-only edit re-render one card while the lane and column objects keep their identity, and
 * lets an axis edit move exactly one key.
 */

export interface KanbanPlacement {
  /** Row identity — `resolveBoardRowId`, the same key the table and list agree on. */
  readonly key: string;
  /** Bucket key of the column the card belongs in. */
  readonly columnKey: string;
  /** Lane key, or the single-lane sentinel when the view groups by nothing. */
  readonly rowKey: string;
}

/** Cheap structural equality — a placement is three strings, so a joined comparison is exact. */
export function kanbanPlacementsEqual(left: readonly KanbanPlacement[], right: readonly KanbanPlacement[]): boolean {

  if (left === right) return true;
  if (left.length !== right.length) return false;

  return left.every((placement, index) => {
    const other = right[index];
    return placement.key === other.key && placement.columnKey === other.columnKey && placement.rowKey === other.rowKey;
  });
}
